import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Input, Form, Row, Col, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { ExecutionSource } from '@/api/generated/schemas';
import type { ActivityOut } from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { ActiveFilters } from '@/components/common/ActiveFilters';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { MultiSelectFilter } from '@/components/common/MultiSelectFilter';
import { TextFilter } from '@/components/common/TextFilter';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { ActivityVerbTag } from '@/components/system/tags';
import { ActivitySubject } from '@/components/system/ActivitySubject';
import { ActivitySubjectFilter } from '@/components/system/ActivitySubjectFilter';
import { orDash } from '@/components/common/show/shared';
import type { ChipScalar } from '@/utils/activeFilters';
import { getFilterValue, isFieldFiltered } from '@/utils/filters';
import { formatDateTime } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';
import { ACTIVITY_OBJECT_TYPES, ACTIVITY_VERBS } from './constants';

const verbOptions = ACTIVITY_VERBS.map((verb) => ({
  label: verb,
  value: verb,
}));

const objectTypeOptions = ACTIVITY_OBJECT_TYPES.map((type) => ({
  label: type,
  value: type,
}));

const SOURCES = Object.values(ExecutionSource);

export const ActivityList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();
  const f = (key: string) => t(`system.activities.fields.${key}`);

  const { tableProps, searchFormProps, setFilters, filters, sorters } =
    useAdminTable<ActivityOut>({
      pagination: { pageSize: 20 },
      resource: 'system.activities',
      syncWithLocation: true,
      onSearch,
      sorters: {
        initial: [{ field: 'created_at', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);
  const crudFilters = filters as CrudFilter[];

  // `source` is filterable but absent from the list projection, so it cannot
  // be a column — it gets its own control on the toolbar instead. A filter
  // restored from the URL arrives as the comma-joined string the dataProvider
  // sent, so normalise before handing it to a multiple `Select`.
  const rawSource = getFilterValue<string[] | string>(
    crudFilters,
    'source',
    'in'
  );
  const sourceValue = Array.isArray(rawSource)
    ? rawSource
    : rawSource
      ? rawSource.split(',')
      : [];

  const formatFilterValue = (field: string, value: ChipScalar) =>
    field === 'source'
      ? t(`system.activities.source.${value}`, String(value))
      : undefined;

  return (
    <List>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('system.activities.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8} md={6} lg={5}>
            <Select
              mode="multiple"
              allowClear
              style={{ width: '100%' }}
              placeholder={f('source')}
              value={sourceValue}
              onChange={(value: string[]) =>
                setFilters(
                  [
                    {
                      field: 'source',
                      operator: 'in',
                      value: value.length ? value : undefined,
                    },
                  ],
                  'merge'
                )
              }
              options={SOURCES.map((source) => ({
                label: t(`system.activities.source.${source}`, source),
                value: source,
              }))}
            />
          </Col>
        </Row>
      </Form>

      <ActiveFilters
        filters={crudFilters}
        setFilters={setFilters}
        labelPrefix="system.activities"
        baseFields={['id', 'created_at']}
        formatValue={formatFilterValue}
      />

      <Table {...rowNav('system.activities', tableProps)} rowKey="id">
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          filtered={isFieldFiltered(crudFilters, 'created_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="created_at"
              filters={crudFilters}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) => orDash(formatDateTime(value))}
        />

        <Table.Column
          dataIndex="verb"
          title={f('verb')}
          sorter
          sortOrder={getSortOrder(sorters, 'verb')}
          filtered={isFieldFiltered(crudFilters, 'verb')}
          filterDropdown={({ confirm, clearFilters }) => (
            <MultiSelectFilter
              field="verb"
              options={verbOptions}
              minWidth={280}
              placeholder={f('verb')}
              filters={crudFilters}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string | null) =>
            orDash(value && <ActivityVerbTag verb={value} />)
          }
        />

        <Table.Column
          dataIndex="object_type"
          title={f('object')}
          sorter
          sortOrder={getSortOrder(sorters, 'object_type')}
          filtered={
            isFieldFiltered(crudFilters, 'object_type') ||
            isFieldFiltered(crudFilters, 'object_id')
          }
          filterDropdown={({ confirm, clearFilters }) => (
            <ActivitySubjectFilter
              typeField="object_type"
              idField="object_id"
              options={objectTypeOptions}
              typePlaceholder={f('object_type')}
              idPlaceholder={f('object_id')}
              filters={crudFilters}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(_, record: ActivityOut) => (
            <ActivitySubject type={record.object_type} id={record.object_id} />
          )}
        />

        <Table.Column
          dataIndex="target_type"
          title={f('target')}
          sorter
          sortOrder={getSortOrder(sorters, 'target_type')}
          filtered={isFieldFiltered(crudFilters, 'target_type')}
          filterDropdown={({ confirm, clearFilters }) => (
            <MultiSelectFilter
              field="target_type"
              options={objectTypeOptions}
              placeholder={f('target_type')}
              filters={crudFilters}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(_, record: ActivityOut) => (
            <ActivitySubject type={record.target_type} id={record.target_id} />
          )}
        />

        <Table.Column
          // Sorts on `actor_id`: `actor_email` resolves through a second join
          // and the backend leaves it out of the sortable set — asking for it
          // is a 422.
          dataIndex="actor_id"
          title={f('actor')}
          sorter
          sortOrder={getSortOrder(sorters, 'actor_id')}
          filtered={isFieldFiltered(crudFilters, 'actor_email')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="actor_email"
              width={240}
              placeholder={t('system.activities.filter.actor_email')}
              filters={crudFilters}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(_, record: ActivityOut) =>
            orDash(
              record.actor_id != null && (
                <ShowIconLink resource="iam.users" id={record.actor_id}>
                  {record.actor_email ?? record.actor_id}
                </ShowIconLink>
              )
            )
          }
        />
      </Table>
    </List>
  );
};
